import * as React from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {AccountAvatar} from 'shells/avatar/account-avatar';
import {Button} from 'shells/button/button';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {AccountCard} from 'shells/peep-cards/account-card/account-card';
import {AccountCardCreateable} from 'shells/peep-cards/account-card/account-card-createable/account-card-createable';
import {Body} from 'shells/typography/body';
import {SectionLabel} from 'shells/typography/section-label';

import {analyticsClient} from '../../../analytics-client';

import {useCreateAccount} from '../../core/select/hooks/use-create-account';
import {updatePeepRelatedLeadStats} from '../../entity-details/helpers';

import {AccountHierarchyFeatureGate} from './account-hierarchy-feature-gate';
import {AddButtons} from './add-buttons';
import {ChildAccounts} from './child-accounts';
import {useGetAccountHierarchy, useSetAccountParent, useRemoveAccountParent} from './graphql/hooks';
import {
    getPotentialAccounts,
    getAccountHierarchySectionHeader,
    updatePageToReflectAccountHierarchyRemoval,
    updatePageToReflectAccountHierarchyAddition,
} from './utils';
import type {AccountInput} from './utils';

import styles from './account-hierarchy.module.css';

type Props = {
    accountId: string;
};

export function AccountHierarchy(props: Props) {
    const [isAddingChild, setIsAddingChild] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const {childAccounts, parentAccount, isLoading, refetch} = useGetAccountHierarchy(
        props.accountId
    );
    const allAccounts = [...(parentAccount ? [parentAccount] : []), ...(childAccounts || [])];
    const allAccountIds = allAccounts.map((account) => account.id);
    const {setAccountParent, error: errorSettingParent} = useSetAccountParent();
    const {createAccount, error: errorSettingChild} = useCreateAccount();
    const {removeAccountParent, error: errorRemovingParent} = useRemoveAccountParent();

    React.useEffect(() => {
        if (errorSettingParent) {
            setErrorMessage(errorSettingParent);
        } else if (errorSettingChild) {
            setErrorMessage(errorSettingChild);
        } else if (errorRemovingParent) {
            setErrorMessage(errorRemovingParent);
        }
    }, [errorSettingParent, errorSettingChild, errorRemovingParent]);

    const clearErrorMessage = () => {
        if (errorMessage) {
            setErrorMessage(null);
        }
    };

    const trackAddedSubsequentChild = () => {
        analyticsClient.track(
            analyticsClient.EVENTS.TRACK_ACCOUNT_HIERARCHY_CHILD_ADDED_TO_EXISTING_PARENT
        );
    };

    const onSelectAccount = (id: string, isAddingParent: boolean) => {
        clearErrorMessage();
        const accountId = isAddingParent ? props.accountId : id;
        const parentAccountId = isAddingParent ? id : props.accountId;

        return setAccountParent({accountId, parentAccountId, isAddingParent}).then(() => {
            updatePeepRelatedLeadStats();

            return updatePageToReflectAccountHierarchyAddition(props.accountId, parentAccountId);
        });
    };

    const onCreateAccount = (input: {
        accountInput: AccountInput;
        parentAccountId?: string | null | undefined;
        childAccountId?: string | null | undefined;
    }) => {
        clearErrorMessage();
        const trimmedAddress = input.accountInput.address?.trim();

        return createAccount({
            name: input.accountInput.name,
            description: input.accountInput.description,
            addresses: trimmedAddress
                ? [
                      {
                          address: {},
                          singleLineAddress: trimmedAddress,
                      },
                  ]
                : undefined,
            parentAccountId: input.parentAccountId,
            childAccountIds: input.childAccountId ? [input.childAccountId] : undefined,
        }).then(() => {
            updatePeepRelatedLeadStats();

            return updatePageToReflectAccountHierarchyAddition(
                props.accountId,
                input.parentAccountId
            );
        });
    };

    const onRemoveParent = (accountId: string) => {
        clearErrorMessage();
        removeAccountParent({accountId}).then(() => {
            refetch();
            updatePageToReflectAccountHierarchyRemoval(props.accountId, accountId);
            updatePeepRelatedLeadStats();
        });
    };

    const hasAccountHierarchyCapability = NutClientConfig.hasAccountHierarchy;
    const hasChild = !isLoading && childAccounts.length > 0;
    const hasParent = !isLoading && Boolean(parentAccount);
    const shouldShowAddChildButton = hasAccountHierarchyCapability && !isAddingChild && hasChild;
    const shouldShowAddButtons =
        hasAccountHierarchyCapability && !isLoading && !isAddingChild && !hasChild && !hasParent;

    if (!hasAccountHierarchyCapability) {
        return <AccountHierarchyFeatureGate isLoading={isLoading} />;
    }

    return (
        <div className={styles.container}>
            <SectionLabel>
                <div className={styles.header}>
                    {isLoading ? null : getAccountHierarchySectionHeader(hasParent, hasChild)}
                    {shouldShowAddChildButton ? (
                        <Button
                            variant='text-primary'
                            onClick={() => {
                                clearErrorMessage();
                                setIsAddingChild(true);
                            }}
                            noPadding={true}
                        >
                            Add a child
                        </Button>
                    ) : undefined}
                </div>
            </SectionLabel>
            {isLoading && <LoadingPage />}
            {errorMessage && (
                <div className='mb-8'>
                    <Body size='small' color='error'>
                        {errorMessage}
                    </Body>
                </div>
            )}
            {shouldShowAddButtons && (
                <AddButtons
                    accountId={props.accountId}
                    onSelectAccount={onSelectAccount}
                    onCreateAccount={onCreateAccount}
                    alreadyAddedAccountIds={allAccountIds}
                    refetch={refetch}
                />
            )}
            <StackLegacy spacing={8}>
                {parentAccount ? (
                    <AccountCard
                        name={parentAccount.name}
                        icon={<AccountAvatar avatarUrl={parentAccount.avatarUrl} />}
                        accountUrl={parentAccount.htmlUrlPath}
                        description={parentAccount.description}
                        location={parentAccount.primaryAddressRegion}
                        toolbarProps={{
                            options: [
                                {
                                    id: 'remove-association',
                                    tooltipText: 'Remove association',
                                    icon: TrashRegularIcon,
                                    onClick: () => onRemoveParent(props.accountId),
                                },
                            ],
                        }}
                    />
                ) : undefined}
                {childAccounts && childAccounts.length ? (
                    <ChildAccounts
                        parentAccountId={props.accountId}
                        childAccounts={childAccounts}
                        onRemoveParent={onRemoveParent}
                        isAddingChild={isAddingChild}
                    />
                ) : undefined}
                {isAddingChild ? (
                    <AccountCardCreateable
                        onSelectAccount={(account: DisplayOptionEntity) => {
                            onSelectAccount(account.id, false).then(() => {
                                setIsAddingChild(false);
                                trackAddedSubsequentChild();
                            });
                        }}
                        onSubmit={(input: AccountInput) => {
                            onCreateAccount({
                                accountInput: input,
                                parentAccountId: props.accountId,
                            }).then(() => {
                                setIsAddingChild(false);
                                trackAddedSubsequentChild();
                                refetch();
                            });
                        }}
                        onSearchPotentialAccounts={(input: AccountInput) =>
                            getPotentialAccounts({
                                accountInput: input,
                                isAddingParent: false,
                                accountId: props.accountId,
                                alreadyAddedAccounts: allAccountIds,
                            })
                        }
                        onClose={() => {
                            clearErrorMessage();
                            setIsAddingChild(false);
                        }}
                    />
                ) : undefined}
            </StackLegacy>
        </div>
    );
}
